import { ExternalLink, ShoppingCart } from 'lucide-react';
import { Modal } from '@/shared/ui';
import { fmtDate, money } from '@/shared/formatters';
import type { ProcurementOrderRow } from '@contracts/procurement';
import { SlaBadge, StateBadge, UrgencyBadge } from '../components/StatusBadges';

interface Row {
  label: string;
  value: React.ReactNode;
}

/** Detalle read-only de una orden de compra. Las acciones (autorizar/comprar)
 *  viven en Odoo — aquí solo se consulta y se ofrece el deep-link. */
export function PurchaseDrilldownModal({
  order,
  onClose,
}: {
  order: ProcurementOrderRow | null;
  onClose: () => void;
}) {
  if (!order) return null;

  const rows: Row[] = [
    { label: 'Folio', value: order.name },
    { label: 'Solicitante', value: order.requester },
    { label: 'Compañía', value: order.company ?? '—' },
    { label: 'Departamento', value: order.department ?? '—' },
    { label: 'Proveedor', value: order.partner ?? '—' },
    { label: 'Estatus', value: <StateBadge state={order.state} /> },
    { label: 'Urgencia', value: <UrgencyBadge urgency={order.urgency} /> },
    { label: 'SLA', value: <SlaBadge sla={order.slaState} /> },
    { label: 'Monto', value: money(order.amount) },
    { label: 'Creada', value: fmtDate(order.createdAt) },
    { label: 'Autorizada', value: order.authorizedAt ? fmtDate(order.authorizedAt) : '—' },
    { label: 'Límite de autorización', value: fmtDate(order.authorizationDeadline) || '—' },
    { label: 'Antigüedad', value: `${order.ageDays} días` },
    ...(order.rejectionReason
      ? [{ label: 'Motivo de rechazo', value: order.rejectionReason }]
      : []),
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={`Orden ${order.name}`}
      headerGradient="from-[#5c1212] via-[#7d1818] to-[#a52121]"
      icon={<ShoppingCart className="h-5 w-5" />}
      maxWidth="max-w-2xl"
      headerRight={
        order.odooUrl ? (
          <a
            href={order.odooUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-lg bg-white/15 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/25"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Abrir en Odoo
          </a>
        ) : undefined
      }
    >
      <div className="overflow-y-auto p-5">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {r.label}
              </dt>
              <dd className="text-right text-sm font-medium text-slate-700">{r.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Modal>
  );
}
